import { styled } from '@mui/material/styles';
import type { TextFieldProps as MuiTextFieldProps } from '@mui/material/TextField';
import MuiTextField from '@mui/material/TextField';
import type { ReactNode } from 'react';
import { forwardRef, useId } from 'react';
import { intent, sizes, spacingTokens, text } from '../../theme/tokens/index.ts';
import { FieldLabelTab, FOCUS_SELECTOR, resolveFieldState, StyledFieldRoot } from './FieldLabelTab.tsx';

export interface TextFieldProps extends Omit<MuiTextFieldProps, 'variant' | 'size'> {
  variant?: 'outlined';
  size?: 'medium';
  /** Icon node shown before the helper text (e.g. an info or error icon at 12px). */
  helperIcon?: ReactNode;
}

/**
 * Root = {@link StyledFieldRoot} (the shared label-tab host) plus this component's helper-text colour
 * rules. Those are state-driven but NOT interactive, except the focused one, which is CSS
 * (`:focus-within`) rather than React state — see the note in FieldLabelTab.tsx.
 *
 * Error keeps MUI's own red (`.Mui-error`), so it is deliberately absent here.
 */
const StyledTextFieldRoot = styled(StyledFieldRoot)(({ fieldState }) => ({
  ...(fieldState === 'success' && {
    '& .MuiFormHelperText-root': { color: intent.success.text },
  }),
  ...(fieldState === 'readOnly' && {
    '& .MuiFormHelperText-root': { color: text.disabled },
  }),
  ...(fieldState === 'default' && {
    [`${FOCUS_SELECTOR} .MuiFormHelperText-root`]: { color: text.primary },
  }),
}));

const StyledHelperRow = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: spacingTokens.sp4,
});

/** Sizes any Phosphor/SVG child to 12px via `font-size`; `lineHeight: 0` keeps the row tight. */
const StyledHelperIcon = styled('span')({
  display: 'inline-flex',
  fontSize: sizes.height.h12,
  lineHeight: 0,
  flexShrink: 0,
});

// MUI's FormControl margins, applied to the layout root so the space sits above the label tab
// (and below the helper row) instead of between the tab and the input.
const MARGIN_SX = {
  dense: { marginTop: `${spacingTokens.sp8}px`, marginBottom: `${spacingTokens.sp4}px` },
  normal: { marginTop: `${spacingTokens.sp16}px`, marginBottom: `${spacingTokens.sp8}px` },
} as const;

export const TextField = forwardRef<HTMLDivElement, TextFieldProps>(function TextField(props, ref) {
  const {
    label,
    required,
    disabled,
    error,
    color,
    slotProps,
    helperText,
    helperIcon,
    fullWidth,
    margin,
    className,
    sx,
    ...rest
  } = props;

  const labelId = useId();

  // Read-only can arrive two ways: directly on the `input` slot (plain TextField usage), or on
  // the `htmlInput` slot — which is how MUI's <Autocomplete readOnly> forwards it (it only sets
  // the native input's `readonly` attribute, never InputBase's `readOnly` prop). Detect both, then
  // re-assert `readOnly` on the `input` slot below so InputBase adds `.MuiInputBase-readOnly` and
  // the borderless/no-fill read-only theme overrides actually fire.
  const readOnly = Boolean(
    (slotProps?.input as { readOnly?: boolean } | undefined)?.readOnly ||
      (slotProps?.htmlInput as { readOnly?: boolean } | undefined)?.readOnly
  );

  // Priority chain readOnly > disabled > error > success; hover and focus are CSS on the root.
  const fieldState = resolveFieldState({ readOnly, disabled, error, success: color === 'success' });

  const formattedHelperText =
    helperIcon && helperText !== undefined && helperText !== null && helperText !== '' ? (
      <StyledHelperRow>
        <StyledHelperIcon>{helperIcon}</StyledHelperIcon>
        <span>{helperText}</span>
      </StyledHelperRow>
    ) : (
      helperText
    );

  return (
    // The root is the component's LAYOUT ROOT (it carries the label tab + helper row), so
    // `ref` / `className` / `sx` / `margin` land here — a consumer's margin/grid-area targets the
    // whole field, not the inner MuiTextField.
    <StyledTextFieldRoot
      ref={ref}
      className={className}
      fieldState={fieldState}
      fullWidth={fullWidth}
      sx={[margin && margin !== 'none' ? MARGIN_SX[margin] : {}, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {label && <FieldLabelTab label={label} labelId={labelId} required={required} readOnly={readOnly} />}
      <MuiTextField
        {...rest}
        variant="outlined"
        size="medium"
        label={undefined}
        disabled={disabled}
        error={error}
        color={color}
        fullWidth={fullWidth}
        helperText={formattedHelperText}
        slotProps={{
          ...slotProps,
          // Re-assert readOnly on the InputBase (`input` slot) so `.MuiInputBase-readOnly` is
          // applied even when it arrived via `htmlInput` (Autocomplete). Preserves any existing
          // `input` slot props (e.g. Autocomplete's endAdornment / ref).
          ...(readOnly ? { input: { ...(slotProps?.input as object | undefined), readOnly: true } } : {}),
          htmlInput: {
            ...(slotProps?.htmlInput as object | undefined),
            ...(label ? { 'aria-labelledby': labelId } : {}),
          },
        }}
      />
    </StyledTextFieldRoot>
  );
});
